import * as io from "io-ts";
import React from "react";
import { HIDE_ON_EXPORT } from "@ui/lib/downloadPage";
import ButtonIcon from "@ui/ButtonIconV2";
import { FileCopyLine, FileTextLine, GitCommitLine, QuestionLine } from "@ui/IconV2/react";
import { translationLocale } from "../../../i18n/locale";
import Menu from "@ui/Menu";
import Option, { OptionGroup } from "@ui/Option";
import Badge from "@ui/Badge";
import t from "../../../i18n";
import { css } from "@linaria/core";
import useToasts from "@ui/Toast/useToasts";
import { colors } from "@ui/Theme";
import createGlobalState from "@ui/lib/createGlobalState";
import { getHelpCenterUrl } from "../../../lib/helpCenter";
import { HELP_CENTER_RELEASE_LINKS } from "./helpCenterReleaseLinks";

const HELP_CENTER_LAST_SEEN_RELEASE_STORAGE_KEY = "helpCenter:lastSeenReleasePath";
const supportEmail = "support@metroscope.tech";

const useLastSeenReleasePath = createGlobalState({
  storage: "local",
  key: HELP_CENTER_LAST_SEEN_RELEASE_STORAGE_KEY,
  type: io.union([io.string, io.null]),
  defaultValue: null,
  scoped: false,
});

const UserGuideButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => {
  const { addToast } = useToasts();
  const documentationUrl = getHelpCenterUrl("welcome/", translationLocale);
  const latestReleases = HELP_CENTER_RELEASE_LINKS.slice(0, 3);
  const latestReleasePath = HELP_CENTER_RELEASE_LINKS[0]?.path ?? null;
  const [lastSeenReleasePath, setLastSeenReleasePath] = useLastSeenReleasePath({});
  const dateFormatter = React.useMemo(
    () =>
      new Intl.DateTimeFormat(translationLocale === "fr" ? "fr-FR" : "en-US", {
        day: "2-digit",
        month: "short",
      }),
    [],
  );
  const hasUnreadLatestRelease =
    latestReleasePath !== null && lastSeenReleasePath !== latestReleasePath;

  const handleCopySupportEmail = React.useCallback(async () => {
    try {
      await window.navigator.clipboard.writeText(supportEmail);
      addToast("success", t("header.help_center.support.copy_success"));
    } catch {
      addToast("error", t("header.help_center.support.copy_error"));
    }
  }, [addToast]);

  const markLatestReleaseAsRead = React.useCallback(() => {
    setLastSeenReleasePath(latestReleasePath);
  }, [latestReleasePath, setLastSeenReleasePath]);

  return (
    <Menu
      arrowWidth={0}
      placement="bottom"
      className={helpCenterMenu}
      overlay={
        <OverlayWrapper
          documentationUrl={documentationUrl}
          latestReleases={latestReleases}
          hasUnreadLatestRelease={hasUnreadLatestRelease}
          dateFormatter={dateFormatter}
          onCopySupportEmail={handleCopySupportEmail}
          onLatestReleaseOpen={markLatestReleaseAsRead}
        />
      }
    >
      <ButtonIcon
        ref={ref}
        className={`${HIDE_ON_EXPORT} ${className ?? ""}`.trim()}
        theme="dark"
        title={t("header.help_center.title")}
        aria-label={
          hasUnreadLatestRelease
            ? `${t("header.help_center.title")} - ${t("header.help_center.releases.unread_aria")}`
            : t("header.help_center.title")
        }
        {...props}
      >
        <span className={questionIconWrapper}>
          <QuestionLine size="m" />
          {hasUnreadLatestRelease && <span className={releaseUnreadDot} aria-hidden />}
        </span>
      </ButtonIcon>
    </Menu>
  );
});

export default React.memo(UserGuideButton);

const OverlayWrapper = ({
  documentationUrl,
  latestReleases,
  hasUnreadLatestRelease,
  dateFormatter,
  onCopySupportEmail,
  onLatestReleaseOpen,
}: {
  documentationUrl: string;
  latestReleases: typeof HELP_CENTER_RELEASE_LINKS;
  hasUnreadLatestRelease: boolean;
  dateFormatter: Intl.DateTimeFormat;
  onCopySupportEmail: () => Promise<void>;
  onLatestReleaseOpen: () => void;
}) => (
  <>
    <OptionGroup title={t("header.help_center.title")}>
      <Option href={documentationUrl} target="_blank" rel="noopener noreferrer" closeOnClick>
        <FileTextLine />
        {t("header.help_center.documentation")}
      </Option>
    </OptionGroup>
    <OptionGroup title={t("header.help_center.releases.title")}>
      {latestReleases.map(({ title, path, releasedAt }, index) => (
        <Option
          key={path}
          href={getHelpCenterUrl(path, translationLocale)}
          target="_blank"
          rel="noopener noreferrer"
          closeOnClick
          onClick={index === 0 ? onLatestReleaseOpen : undefined}
        >
          <GitCommitLine />
          <div className={releaseContent}>
            <span className={releaseTitle}>{title[translationLocale]}</span>
            <div className={releaseBadgeSlot}>
              {index === 0 && hasUnreadLatestRelease && (
                <Badge
                  size="s"
                  backgroundColor={colors.lightNitrogen500_Main}
                  color={colors.hydrogen}
                  className={releaseNewBadge}
                >
                  {t("header.help_center.releases.new_badge")}
                </Badge>
              )}
            </div>
            <span className={releaseDate}>
              {dateFormatter.format(new Date(`${releasedAt}T00:00:00Z`))}
            </span>
          </div>
        </Option>
      ))}
    </OptionGroup>
    <div className={supportSection}>
      <div className={supportText}>{t("header.help_center.support.message")}</div>
      <div className={supportEmailRow}>
        <span className={supportEmailText}>{supportEmail}</span>
        <ButtonIcon
          theme="light"
          className={supportCopyButton}
          onClick={() => void onCopySupportEmail()}
          title={t("header.help_center.support.copy_aria")}
          aria-label={t("header.help_center.support.copy_aria")}
        >
          <FileCopyLine />
        </ButtonIcon>
      </div>
    </div>
  </>
);

const helpCenterMenu = css`
  min-width: 272px;
`;

const supportText = css`
  color: ${colors.carbon800};
  font-size: 12px;
  line-height: 1.4;
  margin: 0 8px 8px;
  max-width: 240px;
  overflow-wrap: anywhere;
  white-space: normal;
`;

const supportEmailRow = css`
  align-items: center;
  display: flex;
  gap: 4px;
  margin: 0 8px 8px;
`;

const supportEmailText = css`
  color: ${colors.carbon900};
  font-size: 12px;
`;

const supportCopyButton = css`
  padding: 4px;
`;

const questionIconWrapper = css`
  display: inline-flex;
  position: relative;
`;

const releaseUnreadDot = css`
  background-color: ${colors.lightNitrogen500_Main};
  border-radius: 999px;
  height: 8px;
  position: absolute;
  right: -1px;
  top: -1px;
  width: 8px;
`;

const releaseContent = css`
  align-items: center;
  column-gap: 14px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  min-width: 0;
  width: 100%;
`;

const releaseTitle = css`
  align-items: center;
  display: inline-flex;
  min-width: 0;
`;

const releaseNewBadge = css`
  font-size: 10px;
  line-height: 1;
  padding: 2px 6px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const releaseBadgeSlot = css`
  align-items: center;
  display: inline-flex;
  justify-content: flex-start;
`;

const releaseDate = css`
  color: ${colors.carbon700};
  font-size: 12px;
  white-space: nowrap;
`;

const supportSection = css`
  margin-top: 16px;
`;
